/**
 * Volumetric calibration worksheet arithmetic (spec Appendix C, steps 4-8).
 *
 *   4. Obtain weight and record the result.
 *   5. Photograph the batch computer screen for counts/kg and number of counts.
 *   6. Calculate counts/kg x actual counts.
 *   7. Back-check: actual weight / actual counts.
 *   8. Check water: litres dispensed = weight in kg, expect +/-1%.
 *
 * Two separate activities, which this module keeps separate:
 *
 *   TRIALS (3)  record only — reading_kg, counts, and the wt/count they imply.
 *               Nothing to judge them against.
 *   CHECKS (2)  the verification. An entered check wt/count off the batch
 *               computer, against a measured counts/kg pair, judged at the
 *               +/-2% SITE tolerance (NOT CSA A23.1's +/-1%).
 *
 * The workbook lays these out side by side in columns B-D (trials) and E-H
 * (checks). Modelling all of it on the trial rows implied three checks and
 * produced a verdict cell that could never be filled.
 *
 * Workbook: C:\Users\geoff\Dropbox\SCI\Volumentric Calibration.xlsx
 * Spec: docs/superpowers/specs/2026-07-29-calibration-checks-design.md
 */

import {
  CHECK_TOLERANCE_FRACTION,
  CHECK_WT_PER_COUNT_DECIMALS,
  type CalibrationCheckComputed,
  type CalibrationComputed,
  type CalibrationTrialComputed
} from '../types';
import { divide, mean, roundTo, type Numeric } from './numbers';
import { toleranceCheck, type ToleranceResult } from './tolerance';

/**
 * Wt/count for one trial: `reading_kg / counts` (Appendix C step 7).
 *
 * Returns `null` when either input is missing or `counts` is zero.
 *
 * Real worksheet values: 52 kg over 685 counts -> 0.075912 kg/count.
 */
export function wtPerCount(readingKg: Numeric, counts: Numeric): number | null {
  return divide(readingKg, counts);
}

/**
 * Actual wt/count for a calibration check:
 * `ROUND(actual_kg / actual_counts, CHECK_WT_PER_COUNT_DECIMALS)`.
 *
 * UNLIKE every other rounding in this codebase, the value this returns IS the
 * judged value, not a display figure. `roundTo` and `wcRatioDisplay` both warn
 * that a rounded number must never feed another calculation; this is the one
 * documented exception, because the workbook rounds before comparing — `F9`
 * consumes `F6`, not `F7/F5` — so the record follows the same order of
 * operations as the paperwork it replaces.
 *
 * We round to 4 dp where the workbook uses 3 (`F6` = `ROUND(F7/F5,3)`). At 3 dp
 * a step is ~1.32% of a 0.076 reading, which swamped the variance and made the
 * percentage almost non-discriminating; at 4 dp a step is ~0.13%. The trade is
 * explicit: this no longer reproduces the sheet's DISPLAYED wt/count (0.0734
 * against its 0.073), and Check 1 reads -3.42% rather than -3.95%. The reading
 * itself is untouched — only the rounding applied before the comparison.
 *
 * Returns `null` when either input is missing or `actualCounts` is zero.
 */
export function actualWtPerCount(actualKg: Numeric, actualCounts: Numeric): number | null {
  return roundTo(divide(actualKg, actualCounts), CHECK_WT_PER_COUNT_DECIMALS);
}

/**
 * Calibration check variance, judged at the +/-2% SITE tolerance.
 *
 * `(actual wt/count - check wt/count) / check wt/count`, where the check
 * wt/count is the figure entered off the batch computer.
 *
 * NOT CSA A23.1. Never label the result as such — see
 * CHECK_TOLERANCE_FRACTION.
 *
 * The workbook applies this rule in `H9` = `(H6-E6)/E6`. Its `F9` divides by
 * `C6` — Trial 2's wt/count — which is a copy-paste error and is deliberately
 * not reproduced: it is the only reason Check 1 reads -3.83% on the paperwork
 * rather than -3.95%.
 *
 * Returns `null` when either side is missing or the reference is zero. An
 * unanswered check is not a failed check.
 */
export function checkVariance(
  actualWtPerCountValue: Numeric,
  checkWtPerCount: Numeric
): ToleranceResult | null {
  return toleranceCheck(actualWtPerCountValue, checkWtPerCount, CHECK_TOLERANCE_FRACTION);
}

/**
 * Water check (Appendix C step 8): litres dispensed should equal weight in kg
 * within +/-1%. `ratio` is `kg / litres`, matching the workbook's cell K10
 * (`=[KG]/[Litres]`).
 *
 * Returns `null` when either input is missing or `litres` is zero.
 *
 * Real worksheet values (row 10): 28.9 litres against 29.1 kg -> ratio 1.00692,
 * +0.692%, pass.
 *
 * Direction matters. This used to compute `litres / kg` AND be fed the two
 * values transposed (29.1 as litres, 28.9 as kg). The two errors cancelled to
 * the same 1.00692, so the fixture looked right while the model was wrong: real
 * readings entered the correct way round printed 0.9931 and a negative
 * deviation where the paperwork prints 1.0069 and a positive one.
 */
export function waterCheck(litres: Numeric, kg: Numeric): ToleranceResult | null {
  return toleranceCheck(kg, litres);
}

/** One calibration trial row, as loosely typed as the form that fills it. */
export interface CalibrationTrialInput {
  /** 1..3 */
  trial_no: number;
  reading_kg?: Numeric;
  counts?: Numeric;
}

/** One calibration check row, as loosely typed as the form that fills it. */
export interface CalibrationCheckInput {
  /** 1..2 */
  check_no: number;
  check_counts?: Numeric;
  check_kg?: Numeric;
  check_wt_per_count?: Numeric;
  actual_counts?: Numeric;
  actual_kg?: Numeric;
}

/** The sheet-level half of the calibration worksheet. */
export interface CalibrationInput {
  /** kg per count actually adopted for this pour. */
  trial_wt_per_count_used?: Numeric;
  trial_water_litres?: Numeric;
  trial_water_kg?: Numeric;
}

/**
 * Everything computed for one trial row: its wt/count, and nothing else.
 *
 * A trial carries no verdict. It is a record of what came off the scale and
 * what the computer counted; the verification against those figures is
 * `calibrationCheckComputed`.
 */
export function calibrationTrialComputed(
  trial: CalibrationTrialInput
): CalibrationTrialComputed {
  return {
    trial_no: trial.trial_no,
    wt_per_count: wtPerCount(trial.reading_kg, trial.counts)
  };
}

/**
 * Everything computed for one check row.
 *
 * `check_counts` and `check_kg` are recorded but not used here — see
 * `CalibrationCheck` for why the Check side's three figures cannot be assumed
 * consistent with each other.
 */
export function calibrationCheckComputed(
  check: CalibrationCheckInput
): CalibrationCheckComputed {
  const actual = actualWtPerCount(check.actual_kg, check.actual_counts);
  return {
    check_no: check.check_no,
    actual_wt_per_count: actual,
    variance: checkVariance(actual, check.check_wt_per_count)
  };
}

/**
 * Everything computed for the calibration worksheet as a whole.
 *
 * `wt_per_count_mean` is the mean of the trials that have a wt/count, and is
 * `null` when none do. Trials and checks are reported in their number order.
 */
export function calibrationComputed(
  calibration: CalibrationInput,
  trials: readonly CalibrationTrialInput[],
  checks: readonly CalibrationCheckInput[] = []
): CalibrationComputed {
  const computedTrials = [...trials]
    .sort((a, b) => a.trial_no - b.trial_no)
    .map((trial) => calibrationTrialComputed(trial));

  const computedChecks = [...checks]
    .sort((a, b) => a.check_no - b.check_no)
    .map((check) => calibrationCheckComputed(check));

  return {
    trials: computedTrials,
    wt_per_count_mean: mean(computedTrials.map((t) => t.wt_per_count)),
    checks: computedChecks,
    water_check: waterCheck(calibration.trial_water_litres, calibration.trial_water_kg)
  };
}
